import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { useApplication, useAddPlan, useUpdatePlan, useDeletePlan } from "../../api/applications";
import { useToast } from "../../components/ToastProvider";
import { StatusTag } from "../../components/StatusTag";
import { formatBRL } from "../../utils/money";
import { Plan } from "../../types/dtos";

function PlanRow({ plan }: { plan: Plan }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(plan.name);
  const [monthlyValue, setMonthlyValue] = useState(String(plan.monthlyValue));
  const [billingCycle, setBillingCycle] = useState<"MENSAL" | "ANUAL">(plan.billingCycle);
  const update = useUpdatePlan();
  const deletePlan = useDeletePlan();
  const toast = useToast();

  async function handleDelete() {
    if (!window.confirm(`Excluir o plano "${plan.name}"? Essa ação não pode ser desfeita.`)) return;
    try {
      await deletePlan.mutateAsync(plan.id);
      toast("Plano excluído.");
    } catch (err) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast(message ?? "Não foi possível excluir — desative em vez disso.");
    }
  }

  async function handleSave() {
    const numericValue = Number(monthlyValue.replace(",", "."));
    if (!name.trim() || !monthlyValue.trim() || Number.isNaN(numericValue) || numericValue < 0) {
      toast("Informe um nome e um valor válido (zero ou maior) para o plano.");
      return;
    }
    try {
      await update.mutateAsync({ planId: plan.id, input: { name, monthlyValue: numericValue, billingCycle } });
      toast("Plano atualizado.");
      setEditing(false);
    } catch {
      toast("Não foi possível salvar o plano.");
    }
  }

  async function handleToggleActive() {
    try {
      await update.mutateAsync({ planId: plan.id, input: { active: !plan.active } });
      toast(plan.active ? "Plano desativado." : "Plano reativado.");
    } catch {
      toast("Não foi possível atualizar o plano.");
    }
  }

  if (editing) {
    return (
      <div className="d-flex align-items-center gap-2 py-2 flex-wrap" style={{ borderBottom: "1px solid var(--color-divider)" }}>
        <input className="form-control form-control-sm" style={{ flex: 2, minWidth: 120 }} value={name} onChange={(e) => setName(e.target.value)} />
        <input
          className="form-control form-control-sm"
          style={{ width: 100 }}
          value={monthlyValue}
          onChange={(e) => setMonthlyValue(e.target.value)}
        />
        <select
          className="form-select form-select-sm"
          style={{ width: 100 }}
          value={billingCycle}
          onChange={(e) => setBillingCycle(e.target.value as "MENSAL" | "ANUAL")}
        >
          <option value="MENSAL">Mensal</option>
          <option value="ANUAL">Anual</option>
        </select>
        <button type="button" className="btn btn-danger btn-sm" style={{ fontSize: 12 }} onClick={() => setEditing(false)}>
          Cancelar
        </button>
        <button type="button" className="btn btn-success btn-sm" disabled={update.isPending} onClick={handleSave}>
          Salvar
        </button>
      </div>
    );
  }

  return (
    <div className="d-flex align-items-center gap-3 py-2 flex-wrap" style={{ borderBottom: "1px solid var(--color-divider)", fontSize: 13.5 }}>
      <span className="flex-grow-1" style={{ fontFamily: "var(--font-heading)", minWidth: 100 }}>
        {plan.name}
      </span>
      <span className="text-secondary" style={{ fontSize: 12 }}>
        {plan.billingCycle === "MENSAL" ? "Mensal" : "Anual"}
      </span>
      <span className="nt-tabular" style={{ width: 100, textAlign: "right" }}>
        {formatBRL(plan.monthlyValue)}
      </span>
      <StatusTag status={plan.active ? "ATIVO" : "INATIVO"} />
      <button type="button" className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setEditing(true)}>
        Editar
      </button>
      <button type="button" className="btn btn-ghost" style={{ fontSize: 12 }} disabled={update.isPending} onClick={handleToggleActive}>
        {plan.active ? "Desativar" : "Reativar"}
      </button>
      <button type="button" className="btn btn-ghost text-danger" style={{ fontSize: 12 }} disabled={deletePlan.isPending} onClick={handleDelete}>
        Excluir
      </button>
    </div>
  );
}

export function PlansModal({ applicationId, onClose }: { applicationId: number | null; onClose: () => void }) {
  const { data: application, isLoading } = useApplication(applicationId);
  const [newName, setNewName] = useState("");
  const [newValue, setNewValue] = useState("");
  const [newCycle, setNewCycle] = useState<"MENSAL" | "ANUAL">("MENSAL");
  const addPlan = useAddPlan();
  const toast = useToast();

  if (!applicationId) return null;
  const appId = applicationId;

  async function handleAddPlan(e: React.FormEvent) {
    e.preventDefault();
    const numericValue = Number(newValue.replace(",", "."));
    if (!newName.trim() || !newValue.trim() || Number.isNaN(numericValue) || numericValue < 0) {
      toast("Informe um nome e um valor válido (zero ou maior) para o novo plano.");
      return;
    }
    try {
      await addPlan.mutateAsync({ applicationId: appId, input: { name: newName, monthlyValue: numericValue, billingCycle: newCycle } });
      toast("Plano adicionado.");
      setNewName("");
      setNewValue("");
      setNewCycle("MENSAL");
    } catch {
      toast("Não foi possível adicionar o plano.");
    }
  }

  return (
    <Modal backdrop="static" keyboard={false} show onHide={onClose} centered className="nt-dialog" size="lg">
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title style={{ fontSize: 20, fontFamily: "var(--font-heading)" }}>
          Planos · {application?.name ?? "…"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="d-flex flex-column gap-4">
        {isLoading && <div className="text-secondary">Carregando…</div>}

        {application && (
          <div>
            {application.plans.length === 0 && (
              <div className="text-secondary" style={{ fontSize: 13 }}>
                Nenhum plano cadastrado ainda.
              </div>
            )}
            <div className="d-flex flex-column">
              {application.plans.map((plan) => (
                <PlanRow key={plan.id} plan={plan} />
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleAddPlan} className="d-flex flex-column gap-2">
          <label className="form-label mb-0" style={{ fontSize: 12 }}>
            Adicionar plano
          </label>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <input
              className="form-control"
              style={{ flex: 2, minWidth: 140 }}
              placeholder="Nome do plano"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <input
              className="form-control"
              style={{ width: 120 }}
              placeholder="Valor (R$)"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
            />
            <select className="form-select" style={{ width: 110 }} value={newCycle} onChange={(e) => setNewCycle(e.target.value as "MENSAL" | "ANUAL")}>
              <option value="MENSAL">Mensal</option>
              <option value="ANUAL">Anual</option>
            </select>
            <button type="submit" className="btn btn-success" disabled={addPlan.isPending}>
              Adicionar
            </button>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-danger" onClick={onClose}>
          Fechar
        </button>
      </Modal.Footer>
    </Modal>
  );
}
